import { formatCurrency, formatDate } from '@if/core/utils'
import { AreaButton, ControlledChipsField, ControlledPercentField, ErrorHandler, Help, useConfirmDialog } from '@if/mui'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined'
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import { Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Stack, Typography } from '@mui/material'
import {
  PromoPlanDto,
  useDeleteScheduledCustomBuydown,
  useGetCurrentAndScheduledPromoPlans,
  useTerminateActiveCustomBuydown,
} from '@src/data/api/merchants-api/merchants-api'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCustomBuydownDialog, useReschedulePlanDialog, useSetActivePlanEndDateDialog } from './CustomBuydownDialog'
import { getPromoPlanErrorCode, PromoPlanErrorCode } from './promoPlanErrorCodes'

const formatPercent = (value: number) => `${(value * 100).toFixed(2)}%`

const planNotFoundError = (plan: PromoPlanDto): ErrorHandler => (_descriptor, t) => ({
  role: 'contextual',
  message: t('merchant.customBuydown.errors.planNotFound', { planCode: plan.promoCodePrefix || plan.label }),
})

// Minimal form shape this panel needs, shared by the merchant and banner promotion forms.
interface PromoNotificationFormValues {
  promoNotificationEmailList: string[]
  flashPlanRateRebate: number
}

interface Props {
  id: string
  /** Display-only mode, e.g. a child merchant whose parent banner owns the buydown. */
  readOnly?: boolean
  /** Locks only the rate rebate field, e.g. a user without a Buy Down rebate write level. */
  rebateReadOnly?: boolean
}

const StatusPill = ({ status }: { status: PromoPlanDto['status'] }) => {
  const { t } = useTranslation()
  const color = status === 'active' ? 'success' : status === 'expired' ? 'error' : 'warning'
  return <Chip size="small" variant="outlined" color={color} label={t(`merchant.promotionsTab.status.${status}`)} />
}

const FactWithHint = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <Box>
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {hint && <Help title={hint} />}
    </Stack>
    <Typography>{value}</Typography>
  </Box>
)

const BuydownCard = ({
  plan,
  readOnly,
  activePlan,
  scheduledPlan,
}: {
  plan: PromoPlanDto
  readOnly: boolean
  activePlan?: PromoPlanDto
  scheduledPlan?: PromoPlanDto
}) => {
  const { t } = useTranslation()
  const status = plan.status
  const termRates = [...plan.promoTermRates].sort((a, b) => a.termMonths - b.termMonths)

  const planCode = plan.promoCodePrefix || plan.label

  const [deleteScheduledCustomBuydown] = useDeleteScheduledCustomBuydown()
  const confirmDelete = useConfirmDialog<PromoPlanDto>({
    title: t('merchant.customBuydown.deleteConfirmTitle'),
    message: (ctx) =>
      t('merchant.customBuydown.deleteConfirmMessage', {
        planCode: ctx.props.promoCodePrefix || ctx.props.label,
        activeFrom: formatDate(ctx.props.activeFrom),
      }),
    mode: 'destructive',
    onError: {
      notFound: planNotFoundError(plan),
      unknown: (descriptor, t) =>
        getPromoPlanErrorCode(descriptor) === PromoPlanErrorCode.PlanAlreadyApplicable
          ? { role: 'contextual', message: t('merchant.customBuydown.errors.planAlreadyApplicable', { planCode }) }
          : undefined,
    },
    onConfirm: (ctx) => deleteScheduledCustomBuydown({ merchantId: ctx.props.merchantId, promoPlanId: ctx.props.id }),
  })

  const [terminateActiveCustomBuydown] = useTerminateActiveCustomBuydown()
  const confirmTerminate = useConfirmDialog<PromoPlanDto>({
    title: (ctx) =>
      t('merchant.customBuydown.terminateConfirmTitle', { planCode: ctx.props.promoCodePrefix || ctx.props.label }),
    message: t('merchant.customBuydown.terminateConfirmMessage'),
    mode: 'destructive',
    confirmLabel: t('merchant.customBuydown.terminateConfirmButton'),
    onError: {
      notFound: planNotFoundError(plan),
      unknown: (descriptor, t) => {
        const code = getPromoPlanErrorCode(descriptor)
        if (code === PromoPlanErrorCode.PlanNotYetActive) {
          return { role: 'contextual', message: t('merchant.customBuydown.errors.planNotYetActive', { planCode }) }
        }
        if (code === PromoPlanErrorCode.PlanAlreadyEnded) {
          return { role: 'contextual', message: t('merchant.customBuydown.errors.planAlreadyEnded', { planCode }) }
        }
        return undefined
      },
    },
    onConfirm: (ctx) => terminateActiveCustomBuydown({ merchantId: ctx.props.merchantId, promoPlanId: ctx.props.id }),
  })

  const setEndDateDialog = useSetActivePlanEndDateDialog(plan.merchantId, scheduledPlan)
  const rescheduleDialog = useReschedulePlanDialog(plan.merchantId, activePlan)

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack gap={2.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t(
                status === 'scheduled'
                  ? 'merchant.promotionsTab.custom.promotionScheduled'
                  : 'merchant.promotionsTab.custom.promotionApplied',
              )}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" mt={0.5}>
              <Typography variant="h6" component="span" fontFamily="monospace">
                {plan.promoCodePrefix || plan.label}
              </Typography>
              <StatusPill status={status} />
            </Stack>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2 }}>
            <FactWithHint
              label={t('merchant.customBuydown.contribution')}
              value={formatPercent(plan.merchantFeeRate)}
            />
            <FactWithHint label={t('merchant.customBuydown.adminFee')} value={formatPercent(plan.borrowerFeeRate)} />
            <FactWithHint
              label={t('merchant.customBuydown.maxFinancingAmount')}
              value={
                plan.promoTermRates[0]?.maxFinancingAmount
                  ? formatCurrency(plan.promoTermRates[0].maxFinancingAmount)
                  : t('merchant.customBuydown.noMaximum')
              }
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 2,
              py: 1.5,
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <FactWithHint label={t('merchant.promo.activeFrom')} value={formatDate(plan.activeFrom)} />
            <FactWithHint
              label={t('merchant.promo.expiresOn')}
              value={plan.expiresOn ? formatDate(plan.expiresOn) : t('merchant.promo.noExpiresOn')}
            />
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t('merchant.customBuydown.customerRates')}
              </Typography>
              <Help title={t('merchant.customBuydown.customerRatesTooltip')} />
            </Stack>
            <Box sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 1 }}>
              {termRates.map((rate) => (
                <Box
                  key={rate.termMonths}
                  sx={{ textAlign: 'center', bgcolor: 'action.hover', borderRadius: 1, py: 0.75, px: 1 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {rate.termMonths} {t('common.months')}
                  </Typography>
                  <Typography variant="h4" fontSize="1.4rem" fontWeight={700}>
                    {formatPercent(rate.promoRate)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {status === 'scheduled' && !readOnly && (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                size="small"
                startIcon={<EventRepeatOutlinedIcon fontSize="small" />}
                onClick={() => void rescheduleDialog.edit({ plan })}
              >
                {t('merchant.customBuydown.changeDatesAction')}
              </Button>
              <Button
                color="error"
                variant="outlined"
                size="small"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={() => void confirmDelete.open(plan)}
              >
                {t('merchant.customBuydown.deleteAction')}
              </Button>
            </Stack>
          )}

          {status === 'active' && !readOnly && (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                size="small"
                startIcon={<EventBusyOutlinedIcon fontSize="small" />}
                onClick={() => void setEndDateDialog.edit({ plan })}
              >
                {t('merchant.customBuydown.setEndDateAction')}
              </Button>
              <Button
                color="error"
                variant="outlined"
                size="small"
                startIcon={<StopCircleOutlinedIcon fontSize="small" />}
                onClick={() => void confirmTerminate.open(plan)}
              >
                {t('merchant.customBuydown.terminateAction')}
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

/**
 * The "Custom promotion" body, reused by the merchant and banner promotion tabs. The owning
 * entity has at most one Custom Buydown. A scheduled plan (never yet applicable) can be deleted;
 * the active plan cannot. In `readOnly` mode editing, deletion, and the empty-state are suppressed.
 */
const CustomPromotionPanel = ({ id, readOnly = false, rebateReadOnly = false }: Props) => {
  const { t } = useTranslation()
  const { control, trigger } = useFormContext<PromoNotificationFormValues>()
  const [currentAndScheduledPlans, isFetching] = useGetCurrentAndScheduledPromoPlans(id)
  const activePlan = currentAndScheduledPlans.find((plan) => plan.status === 'active')
  const scheduledPlan = currentAndScheduledPlans.find((plan) => plan.status === 'scheduled')
  const dialog = useCustomBuydownDialog(id)

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title={t('merchant.customBuydown.notificationsTitle')}
          subheader={t('merchant.customBuydown.notificationsDescription')}
          slotProps={{
            title: { variant: 'subtitle1', color: 'primary' },
            subheader: { variant: 'body2' },
          }}
        />
        <CardContent sx={{ pt: 0 }}>
          <ControlledChipsField
            name="promoNotificationEmailList"
            control={control}
            trigger={trigger}
            label={t('merchant.customBuydown.notificationEmails')}
            placeholder={t('merchant.customBuydown.notificationEmailsPlaceholder')}
            helperText={t('merchant.customBuydown.notificationEmailsHelper')}
            disabled={readOnly}
            format={(v) => v.toLowerCase()}
          />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader
          title={t('merchant.customBuydown.rebateTitle')}
          subheader={
            <Stack direction="row" alignItems="center" gap={0.5}>
              <span>{t('merchant.customBuydown.rebateDescription')}</span>
              <Help title={t('merchant.customBuydown.rebateTooltip')} />
            </Stack>
          }
          slotProps={{
            title: { variant: 'subtitle1', color: 'primary' },
            subheader: { variant: 'body2' },
          }}
        />
        <CardContent sx={{ pt: 0 }}>
          <ControlledPercentField
            name="flashPlanRateRebate"
            control={control}
            label={t('merchant.customBuydown.rebate')}
            disabled={readOnly || rebateReadOnly}
            fullWidth
            sx={{ maxWidth: 300 }}
          />
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
        {!activePlan && !scheduledPlan ? (
          !readOnly && (
            <AreaButton
              onClick={() => void dialog.create()}
              icon={<AddIcon fontSize="large" />}
              title={t('merchant.customBuydown.emptyTitle')}
              description={t('merchant.customBuydown.emptyDescription')}
            />
          )
        ) : (
          <>
            {activePlan ? (
              <BuydownCard key={activePlan.id} plan={activePlan} readOnly={readOnly} scheduledPlan={scheduledPlan} />
            ) : (
              !readOnly && (
                <AreaButton
                  onClick={() => void dialog.create()}
                  icon={<AddIcon fontSize="large" />}
                  title={t('merchant.customBuydown.emptyTitle')}
                  description={t('merchant.customBuydown.emptyDescription')}
                />
              )
            )}
            {scheduledPlan ? (
              <BuydownCard key={scheduledPlan.id} plan={scheduledPlan} readOnly={readOnly} activePlan={activePlan} />
            ) : (
              !readOnly && (
                <AreaButton
                  onClick={() => void dialog.create()}
                  icon={<AddIcon fontSize="large" />}
                  title={t('merchant.customBuydown.scheduleTitle')}
                  description={t('merchant.customBuydown.scheduleDescription')}
                />
              )
            )}
          </>
        )}
      </Box>
    </>
  )
}

export default CustomPromotionPanel
